import React, { useState } from "react"
import { User } from "lucide-react"
import { needsImageProxy, proxiedImageUrl } from "../utils"

/**
 * Small circular profile/creator avatar. Self-hides (renders nothing) if
 * there's no URL or it fails to load, rather than leaving a broken-image
 * glyph in a tiny circle - `profile_pic_url` is optional (YouTube items
 * never have one; some Instagram reels may not either), so callers can just
 * drop this in without a presence check of their own.
 *
 * `profile_pic_url` normally holds this app's own S3-hosted mirror of
 * Instagram's profile picture (`profile_thumb_url` - see normalize.js),
 * which loads directly; only a raw Instagram CDN URL (if no mirror exists
 * yet for this reel) is routed through the same hotlink-protection-aware
 * proxy `Thumbnail` uses.
 *
 * `placeholder` opts into a generic filled circle instead of self-hiding -
 * for a layout where every row/card must line up the same way whether or not
 * a photo loaded, e.g. a creator grid where a platform's `profile_image_url`
 * is sometimes missing outright, or a dead signed URL. `needsImageProxy`/
 * `proxiedImageUrl` (utils.js) route anything `.heic`/`.heif` through wsrv.nl
 * to get it decoded and re-served as a JPEG a browser can actually render.
 * Small 16px avatars inside a video card's text row don't pass `placeholder`,
 * since a missing avatar there is meant to just collapse the row rather than
 * reserve space for one.
 */
const Avatar = ({ src, alt, size = 18, style, placeholder = false }) => {
  const resolved = needsImageProxy(src) ? proxiedImageUrl(src) : src
  const [failed, setFailed] = useState(!resolved)

  if (failed) {
    if (!placeholder) return null
    return (
      <span
        title={alt || undefined}
        style={{
          width: size, height: size, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(255,255,255,0.08)", color: "#6b7280",
          ...style,
        }}
      >
        <User size={Math.round(size * 0.55)} />
      </span>
    )
  }

  return (
    <img
      src={resolved}
      alt={alt || ""}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, ...style }}
    />
  )
}

export default Avatar
